import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { AppShell3 } from '@/components/app-shell-3';
import { ToolHeader } from '@/components/ToolHeader';
import { PageTransition } from '@/components/PageTransition';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Table, TableCard } from '@/components/application/table/table-fixed';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Edit,
  Share2,
  Trash2,
  MoreHorizontal,
  GitBranch,
  Globe,
  Users,
  Play,
  CheckCircle2,
  Clock,
  ListTodo,
  Calendar,
  Building2,
  Eye,
  XCircle,
  AlertTriangle,
} from 'lucide-react';
import ProcessShareModal from './ProcessShareModal';
import { formatDistanceToNow } from 'date-fns';

// Skeleton items for loading state
const skeletonItems = Array.from({ length: 5 }, (_, i) => ({
  id: `skeleton-${i}`,
  isLoading: true,
}));

export default function ProcessManagementPage() {
  const navigate = useNavigate();
  const { processId } = useParams();
  const { company, can } = useAuth();
  usePageTitle('Process Management');

  // State
  const [process, setProcess] = useState(null);
  const [runs, setRuns] = useState([]);
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingRuns, setLoadingRuns] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Redirect non-admins
  useEffect(() => {
    if (!can('*')) {
      toast.error('Admin access required');
      navigate('/spaces');
    }
  }, [can, navigate]);

  // Fetch process details
  const fetchProcess = useCallback(async () => {
    if (!processId || !company?.id) return;

    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_company_process', {
        p_workflow_id: processId,
        p_company_id: company.id
      });

      if (error) throw error;

      // Transform RPC result to match expected structure
      const processData = data?.[0];
      if (processData) {
        setProcess({
          ...processData,
          user_profiles: {
            first_name: processData.creator_first_name,
            last_name: processData.creator_last_name,
            avatar_url: processData.creator_avatar_url
          }
        });
      }
    } catch (error) {
      console.error('Error fetching process:', error);
      toast.error('Failed to load process');
      navigate('/processes');
    } finally {
      setLoading(false);
    }
  }, [processId, company?.id, navigate]);

  // Fetch workflow runs
  const fetchRuns = useCallback(async () => {
    if (!processId) return;

    setLoadingRuns(true);
    try {
      const { data, error } = await supabase
        .from('checklists_workflow_runs')
        .select(`
          *,
          projects:project_id (
            id,
            name
          ),
          user_profiles:started_by (
            first_name,
            last_name
          )
        `)
        .eq('workflow_id', processId)
        .order('started_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setRuns(data || []);
    } catch (error) {
      console.error('Error fetching runs:', error);
    } finally {
      setLoadingRuns(false);
    }
  }, [processId]);

  // Fetch shares
  const fetchShares = useCallback(async () => {
    if (!processId) return;

    try {
      const { data, error } = await supabase.rpc('get_process_shares', {
        p_workflow_id: processId
      });

      if (error) throw error;
      setShares(data || []);
    } catch (error) {
      console.error('Error fetching shares:', error);
    }
  }, [processId]);

  // Initial load
  useEffect(() => {
    if (processId && can('*')) {
      fetchProcess();
      fetchRuns();
      fetchShares();
    }
  }, [processId, can, fetchProcess, fetchRuns, fetchShares]);

  // Handle share update
  const handleShareUpdate = () => {
    fetchProcess();
    fetchShares();
    setShowShareModal(false);
  };

  // Handle delete
  const handleDelete = async () => {
    if (!company?.id) return;

    setIsDeleting(true);
    try {
      const { error } = await supabase.rpc('delete_company_process', {
        p_workflow_id: processId,
        p_company_id: company.id
      });

      if (error) throw error;

      toast.success('Process deleted successfully');
      navigate('/processes');
    } catch (error) {
      console.error('Error deleting process:', error);
      toast.error('Failed to delete process');
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  // Stats
  const stats = useMemo(() => {
    const activeRuns = runs.filter(r => r.status === 'in_progress').length;
    const completedRuns = runs.filter(r => r.status === 'completed').length;
    const taskCount = process?.tasks?.length || 0;
    return { activeRuns, completedRuns, taskCount, totalRuns: runs.length };
  }, [runs, process]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'in_progress':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">In Progress</Badge>;
      case 'completed':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Completed</Badge>;
      case 'cancelled':
        return <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (!can('*')) {
    return null;
  }

  if (loading) {
    return (
      <AppShell3>
        <div className="min-h-screen flex items-center justify-center">
          <LoadingSpinner />
        </div>
      </AppShell3>
    );
  }

  if (!process) {
    return (
      <AppShell3>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <GitBranch className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">Process not found</h3>
            <Button variant="outline" onClick={() => navigate('/processes')}>
              Back to Processes
            </Button>
          </div>
        </div>
      </AppShell3>
    );
  }

  return (
    <AppShell3>
      <PageTransition>
        <ToolHeader
          spaceName={process.name}
          toolName=""
          currentTool="processes"
          spaceTools={[]}
          actions={
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={() => navigate('/processes')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <Button variant="outline" onClick={() => setShowShareModal(true)}>
                <Share2 className="h-4 w-4 mr-2" />
                Manage Sharing
              </Button>
              <Button variant="outline" onClick={() => navigate(`/processes/editor/${processId}`)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    className="text-destructive"
                    onClick={() => setShowDeleteDialog(true)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Process
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          }
        />

        <div className="max-w-7xl mx-auto mt-4 space-y-6">
          {/* Process Info Card */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <GitBranch className="h-5 w-5" />
                      {process.name}
                    </CardTitle>
                    {process.description && (
                      <CardDescription className="mt-2">
                        {process.description}
                      </CardDescription>
                    )}
                  </div>
                  <Badge
                    variant="outline"
                    className={
                      process.status === 'published'
                        ? 'bg-green-50 text-green-700 border-green-200'
                        : 'bg-amber-50 text-amber-700 border-amber-200'
                    }
                  >
                    {process.status === 'published' ? 'Published' : 'Draft'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">{stats.taskCount}</div>
                    <div className="text-sm text-muted-foreground flex items-center justify-center gap-1">
                      <ListTodo className="h-4 w-4" />
                      Tasks
                    </div>
                  </div>
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">{stats.totalRuns}</div>
                    <div className="text-sm text-muted-foreground flex items-center justify-center gap-1">
                      <Play className="h-4 w-4" />
                      Total Runs
                    </div>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-700">{stats.activeRuns}</div>
                    <div className="text-sm text-blue-600 flex items-center justify-center gap-1">
                      <Clock className="h-4 w-4" />
                      Active
                    </div>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-700">{stats.completedRuns}</div>
                    <div className="text-sm text-green-600 flex items-center justify-center gap-1">
                      <CheckCircle2 className="h-4 w-4" />
                      Completed
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Sharing Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Share2 className="h-4 w-4" />
                  Availability
                </CardTitle>
              </CardHeader>
              <CardContent>
                {process.is_global ? (
                  <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
                    <Globe className="h-6 w-6 text-blue-600" />
                    <div>
                      <div className="font-medium text-blue-900">All Spaces</div>
                      <div className="text-sm text-blue-600">Available globally</div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center gap-3 p-4 bg-purple-50 rounded-lg mb-3">
                      <Users className="h-6 w-6 text-purple-600" />
                      <div>
                        <div className="font-medium text-purple-900">{shares.length} Spaces</div>
                        <div className="text-sm text-purple-600">Shared selectively</div>
                      </div>
                    </div>
                    {shares.length > 0 && (
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {shares.map(share => (
                          <div key={share.project_id} className="flex items-center gap-2 text-sm">
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                            <span className="truncate">{share.project_name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <Button
                  variant="outline"
                  className="w-full mt-4"
                  onClick={() => setShowShareModal(true)}
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Manage Sharing
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Runs Table */}
          <TableCard.Root>
            <TableCard.Header
              title="Workflow Runs"
              badge={`${runs.length} runs`}
            />
            <Table aria-label="Workflow runs">
              <Table.Header>
                <Table.Head id="run" label="Run" isRowHeader className="w-[30%]" />
                <Table.Head id="space" label="Space" className="w-[20%]" />
                <Table.Head id="status" label="Status" className="w-[15%]" />
                <Table.Head id="started" label="Started" className="w-[20%]" />
                <Table.Head id="actions" label="" className="w-[15%]" />
              </Table.Header>

              <Table.Body items={loadingRuns ? skeletonItems : runs}>
                {(run) => (
                  <Table.Row id={run.id}>
                    <Table.Cell>
                      {run.isLoading ? (
                        <Skeleton className="h-4 w-40" />
                      ) : (
                        <div className="font-medium truncate">{run.run_name}</div>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {run.isLoading ? (
                        <Skeleton className="h-4 w-24" />
                      ) : (
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          <span className="truncate">{run.projects?.name || 'Unknown'}</span>
                        </div>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {run.isLoading ? (
                        <Skeleton className="h-5 w-20" />
                      ) : (
                        getStatusBadge(run.status)
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {run.isLoading ? (
                        <Skeleton className="h-4 w-28" />
                      ) : (
                        <div className="text-sm text-muted-foreground">
                          {run.started_at
                            ? formatDistanceToNow(new Date(run.started_at), { addSuffix: true })
                            : '-'}
                        </div>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {run.isLoading ? (
                        <Skeleton className="h-8 w-8" />
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/space/${run.project_id}/workflow-run/${run.id}`)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      )}
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>

            {/* Empty State */}
            {!loadingRuns && runs.length === 0 && (
              <div className="text-center py-12">
                <Play className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-1">No runs yet</h3>
                <p className="text-gray-500">
                  This process hasn't been run by any space yet
                </p>
              </div>
            )}
          </TableCard.Root>
        </div>

        {/* Share Modal */}
        {showShareModal && process && (
          <ProcessShareModal
            process={process}
            open={showShareModal}
            onOpenChange={setShowShareModal}
            onUpdate={handleShareUpdate}
          />
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent className="sm:max-w-md p-0 overflow-hidden">
            <div className="p-6 space-y-4">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-100">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <div className="flex-1 space-y-1">
                  <AlertDialogTitle className="text-lg font-semibold">Delete Process</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone.
                  </AlertDialogDescription>
                </div>
              </div>
              <div className="rounded-lg bg-muted p-4">
                <p className="text-sm text-muted-foreground">
                  Are you sure you want to delete <span className="font-medium text-foreground">"{process?.name}"</span>?
                  {runs.length > 0 && (
                    <> This will also delete all <span className="font-medium text-foreground">{runs.length}</span> workflow runs associated with this process.</>
                  )}
                </p>
              </div>
              <AlertDialogFooter className="gap-3 pt-2">
                <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
                >
                  {isDeleting ? 'Deleting...' : 'Delete Process'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </div>
          </AlertDialogContent>
        </AlertDialog>
      </PageTransition>
    </AppShell3>
  );
}
